import { createContext, useContext, useEffect, useState } from 'react'
import { RequestService } from '../services/RequestService'
import { useSnackbar } from 'notistack'
import { normalizeString } from '../utils/normalizeString'

const AdministrativeActionsModalContext = createContext()

const SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

const STATUS = {
  PENDING: 'pending',
  ACKNOWLEDGED: 'acknowledged',
  COMPLETED: 'completed',
}

export const AdministrativeActionsModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [link, setLink] = useState('')
  const [users, setUsers] = useState([])
  const [userList, setUserList] = useState([])
  const [selectedTab, setSelectedTab] = useState(0)
  const [text, setText] = useState('')
  const [severity, setSeverity] = useState(SEVERITY.LOW)
  const [status, setStatus] = useState(STATUS.PENDING)
  const [loading, setLoading] = useState(true)
  const { enqueueSnackbar } = useSnackbar()

  const openModal = () => {
    setOpen(true)
  }

  const closeModal = () => {
    setText('')
    setSeverity(SEVERITY.LOW)
    setSelectedTab(0)
    setStatus(STATUS.PENDING)
    setOpen(false)
  }

  const removeUserFromList = ({ id }) => {
    const newList = users.filter(user => user.id !== id)
    setUsers(newList)
  }

  const validateDisabledButton = (currentTab) => {
    const sameAttributesCheck =
      users?.length > 0 && text?.length > 0 && severity

    if (currentTab === "notification" && sameAttributesCheck) {
      return false
    }
    if (currentTab === "annotation" && sameAttributesCheck && status) {
      return false
    }
    return true
  }

  const handleSendClick = async (currentTab) => {
    const usersId = users.map(user => user.id)
    const snackbar = () =>
      enqueueSnackbar('Sucesso!', {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      })

    if (currentTab === "notification") {
      await RequestService.createNotification({ users: usersId, message: text, severity })

      snackbar()
      closeModal()
    } else if (currentTab === "annotation") {
      await RequestService.createAnnotation({ users: usersId, message: text, severity, status })

      snackbar()
      closeModal()
    }
  }

  const selectUser = ({ id, name }) => {
    const user = users.find(user => user.id === id)

    if (!user) {
      const withNewUser = [...users, { id, name }]
      const sortedList = sortUserList({ list: withNewUser })

      setUsers(sortedList)
      return
    }
  }

  const sortUserList = ({ list }) => {
    return list.sort((a, b) => {
      const nameA = normalizeString({ term: a.name })
      const nameB = normalizeString({ term: b.name })

      if (nameA < nameB) {
        return -1
      }

      if (nameA > nameB) {
        return 1
      }
    })
  }

  useEffect(() => {
    if (open) {
      setLoading(true)

      const fetchUsers = async () => {
        const { data } = await RequestService.getPreSelectedUsers({
          url: link,
        })

        if (data.length > 0) {
          const sortedList = sortUserList({ list: data })
          setUsers(sortedList)
        } else {
          setUsers([])
        }

        setLoading(false)
      }

      const fetchAllUsers = async () => {
        const { data } = await RequestService.getUsers()

        if (data.length > 0) {
          const sortedList = sortUserList({ list: data })
          setUserList(sortedList)
        } else {
          setUserList(data)
        }

        setLoading(false)
      }

      fetchAllUsers()
      if (link) {
        fetchUsers()
      }
    }
  }, [open, link])

  return (
    <AdministrativeActionsModalContext.Provider
      value={{
        open,
        openModal,
        closeModal,
        setLink,
        selectedTab,
        setSelectedTab,
        text,
        setText,
        severity,
        setSeverity,
        SEVERITY,
        status,
        setStatus,
        STATUS,
        users,
        userList,
        loading,
        removeUserFromList,
        validateDisabledButton,
        handleSendClick,
        selectUser,
      }}
    >
      {children}
    </AdministrativeActionsModalContext.Provider>
  )
}

export const useAdministrativeActionsModal = () => {
  return useContext(AdministrativeActionsModalContext)
}
